import React, { useEffect } from 'react';
import {
  Typography,
  Table,
  TableContainer,
  TableBody,
  TableHead,
  TableRow,
  CircularProgress,
  Grid,
} from '@material-ui/core';
import { isEmpty, get, orderBy } from 'lodash';
import moment from 'moment';

import {
  Box,
  StyledTableCell,
  StyledTableRow,
  ReferralInviteBox,
  Button,
  SnackBar,
} from 'components';
import { errorMessageHandler, formatAmount } from 'common/utils/helpers';
import { getInvestorAllReferral, resendReferInviteToInvestor } from 'services';
import { emailIcon, groupHandsIcon, groupUserIcon } from 'assets';
import { REFERRAL_STATUS, REFERRAL_STATUS_TYPE } from 'common/utils/constants';

import styles from './styles';

const ReferAndEarn = () => {
  const classes = styles();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [resendLoading, setResendLoading] = React.useState<string>();
  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [referralData, setReferralData] = React.useState<any>([]);
  const [openSnackBar, setOpenSnackBar] = React.useState<boolean>(false);

  const headerText = [
    'S/NO',
    'Email of referee',
    'Name of referee',
    'Referral date',
    'Status',
    'Incentive amount',
    'Action',
  ];

  const headers = (array: string[]) => {
    return (
      <>
        {array.map((i: string, n: number) => {
          return <StyledTableCell key={n}>{i}</StyledTableCell>;
        })}
      </>
    );
  };

  const handleCloseSnackBar = (
    event: React.SyntheticEvent | React.MouseEvent,
    reason?: string,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenSnackBar(false);
  };

  const getAllReferralData = () => {
    setIsLoading(true);
    setErrorMessage('');
    getInvestorAllReferral()
      .then((res: any) => {
        setReferralData(orderBy(res, ['createdAt'], ['desc']));
        setIsLoading(false);
      })
      .catch((err: any) => {
        const message: string = errorMessageHandler(err);
        setErrorMessage(message);
        setIsLoading(false);
      });
  };

  const resendInvite = (referralId: any) => {
    setResendLoading(referralId);
    setErrorMessage('');
    resendReferInviteToInvestor({
      referralId: referralId,
    })
      .then((res) => {
        if (!isEmpty(res)) {
          setOpenSnackBar(true);
        }
        setResendLoading('');
      })
      .catch((err: any) => {
        const message: string = errorMessageHandler(err);
        setErrorMessage(message);
        setResendLoading('');
      });
  };

  useEffect(() => {
    getAllReferralData();
  }, []);

  return (
    <>
      <Box className={classes.referAndEarnContainer}>
        <Box className={classes.mainHeader}>
          <Typography className={classes.mainHeaderText}>
            Refer and Earn
          </Typography>
        </Box>
        <Box className={classes.referAndEarnWorkingContainer}>
          <Grid container>
            <Grid item md={12}>
              <Typography variant="body1" className={classes.headerWorkingText}>
                Refer a friend and earn 10% of our carry
              </Typography>
            </Grid>
            <Grid item md={12}>
              <Typography variant="body1" className={classes.workingText}>
                If your friend signs up & invests in any SPV on 8vdX platform,
                10% of 8vdX's carry from that particular investment will be
                rewarded to you. This is limited to just first investment done
                by your friend.
              </Typography>
            </Grid>
          </Grid>
          <Grid container>
            <Grid item md={4}>
              <Grid container direction="column" alignItems="center">
                <Grid item>
                  <Box className={classes.inviteIconBox}>
                    <img
                      src={emailIcon}
                      alt={emailIcon}
                      className={classes.smallIcon}
                    />
                  </Box>
                </Grid>
                <Grid item>
                  <Typography
                    variant="body1"
                    className={classes.inviteMainText}
                  >
                    Invite Friends
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" className={classes.inviteSubText}>
                    Invite colleagues, family, and friends via e-mail.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item md={4}>
              <Grid container direction="column" alignItems="center">
                <Grid item>
                  <Box className={classes.inviteIconBox}>
                    <img
                      src={groupUserIcon}
                      alt={groupUserIcon}
                      className={classes.smallIcon}
                    />
                  </Box>
                </Grid>
                <Grid item>
                  <Typography
                    variant="body1"
                    className={classes.inviteMainText}
                  >
                    Friend Invests
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" className={classes.inviteSubText}>
                    Each friend that accepts your invitation gets an opportunity
                    Read more
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item md={4}>
              <Grid container direction="column" alignItems="center">
                <Grid item>
                  <Box className={classes.inviteIconBox}>
                    <img
                      src={groupHandsIcon}
                      alt={groupHandsIcon}
                      className={classes.smallIcon}
                    />
                  </Box>
                </Grid>
                <Grid item>
                  <Typography
                    variant="body1"
                    className={classes.inviteMainText}
                  >
                    Referral Bonus
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="body1" className={classes.inviteSubText}>
                    You get 10% of our carry for all the first investments done
                    by each of your friends.
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          <Box className={classes.referInviteBox}>
            <ReferralInviteBox
              type="INVESTOR"
              onSuccessInviteCall={getAllReferralData}
              loading={isLoading}
            />
          </Box>
          {errorMessage && (
            <Typography variant="body1" className={classes.errorMessage}>
              {errorMessage}
            </Typography>
          )}
        </Box>
        <Box className={classes.referralDataContainer}>
          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader aria-label="customized table">
              <TableHead className={classes.theadBox}>
                <TableRow>{headers(headerText)}</TableRow>
              </TableHead>
              <TableBody>
                {isLoading ? (
                  <Box className={classes.loader}>
                    <CircularProgress color="inherit" size={40} />
                  </Box>
                ) : !isEmpty(referralData) ? (
                  referralData.map((row: any, index: number) => (
                    <StyledTableRow hover key={row.id}>
                      <StyledTableCell
                        component="th"
                        scope="row"
                        style={{ textAlign: 'left' }}
                      >
                        {index + 1}
                      </StyledTableCell>
                      <StyledTableCell>
                        {get(row, 'refereeEmail')}
                      </StyledTableCell>
                      <StyledTableCell>
                        {get(row, 'refereeName')}
                      </StyledTableCell>
                      <StyledTableCell>
                        {moment(get(row, 'referralDate')).format('DD-MM-YYYY')}
                      </StyledTableCell>
                      <StyledTableCell className={classes.tBodyStatus}>
                        {REFERRAL_STATUS_TYPE[get(row, 'status')]}
                      </StyledTableCell>
                      <StyledTableCell>
                        {get(row, 'carryRewardAmount') > 0
                          ? formatAmount(get(row, 'carryRewardAmount'))
                          : '-'}
                      </StyledTableCell>
                      <StyledTableCell>
                        {get(row, 'status') === REFERRAL_STATUS.INVITED && (
                          <Button
                            name="Resend Invite"
                            className={classes.resendInviteBtn}
                            onClick={() => resendInvite(row.id)}
                            isLoading={resendLoading === row.id}
                            disabled={!isEmpty(resendLoading)}
                          />
                        )}
                      </StyledTableCell>
                    </StyledTableRow>
                  ))
                ) : (
                  <StyledTableRow>
                    <StyledTableCell colSpan={headerText.length}>
                      <Typography
                        variant="body1"
                        className={classes.noRecordText}
                      >
                        No Records Found
                      </Typography>
                    </StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
      <SnackBar
        open={openSnackBar}
        handleClose={handleCloseSnackBar}
        title="The invite has been resent successfully"
        type="success"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      />
    </>
  );
};
export default ReferAndEarn;
